/**
 * PATH: apps/qa/src/pages/AreaExecutionPage.tsx
 * PURPOSE: Area execution orchestrator — mobile-first checklist with swipe, lock, NCR, hold points
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, Lock, Unlock, Loader2, AlertTriangle,
  ChevronUp, ChevronDown, ClipboardList, ListChecks, Plus, Paperclip,
} from 'lucide-react'
import { QaOwnerVerification, QaStatusFilterChips, QaStatusMenu, type QaStatus } from '@quotech/ui'
import { cn } from '@/lib/utils'
import CameraCapture from '@/components/CameraCapture'
import type { ItemWithState } from './area-execution/area-execution-types'
import { useAreaExecution } from './area-execution/useAreaExecution'
import { addAreaItem } from './area-execution/area-execution-api'
import { ProgressBar } from './area-execution/ProgressBar'
import { ItemCard } from './area-execution/ItemCard'
import { CompletionSection } from './area-execution/CompletionSection'
import { EvidenceViewer } from './area-execution/EvidenceGallery'
import { ApplyTemplateModal, LockModal, RequestHoldPointModal } from './area-execution/AreaModals'
import { CreateNCRModal } from './area-execution/CreateNCRModal'

export default function AreaExecutionPage() {
  const {
    areaId, area, items, evidence, templates, navigate,
    areaLoading, itemsLoading, activeIndex, setActiveIndex,
    cameraOpen, setCameraOpen, templateModalOpen, setTemplateModalOpen,
    lockModalOpen, setLockModalOpen, ncrModalOpen, setNcrModalOpen,
    holdPointModalOpen, setHoldPointModalOpen, areaSignOffModalOpen, setAreaSignOffModalOpen,
    selectedEvidence, setSelectedEvidence, isUploadingDoc, listRef, fileInputRef,
    lockMutation, templateMutation, holdPointMutation, areaSignOffMutation, ncrMutation,
    handleDocumentUpload, handleStatusChange, handleAreaStatusChange, handleAddNote, handleEditNote, handleDeleteNote, handleReadingChange, handleTakePhoto, handleCameraCapture, allComplete,
    handleVerifyItem, handleVerifyArea,
    handlePickPhotoFile, handlePickPhotoAsset, handleDeleteEvidence, refreshItems,
  } = useAreaExecution()

  // View state only. Never filter what gets sent, and never renumber: the pairs
  // below keep each item's real index so the active card and the keyboard/swipe
  // navigation still point at the same item they did before the filter.
  const [statusFilter, setStatusFilter] = useState<QaStatus | null>(null)
  const statusCounts = useMemo(
    () => items.reduce<Partial<Record<QaStatus, number>>>((acc, item) => {
      const key = item.status as QaStatus
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    }, {}),
    [items],
  )
  const visibleItems = useMemo(
    () => items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !statusFilter || item.status === statusFilter),
    [items, statusFilter],
  )

  if (areaLoading || itemsLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
  }

  if (!area) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h1 className="text-xl font-bold text-foreground mb-2">Area Not Found</h1>
        <p className="text-muted-foreground mb-4">The requested area does not exist.</p>
        <Link to="/records" className="text-primary hover:underline">Back to Records</Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted transition-colors"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-foreground truncate">{area.area_name || area.name}</h1>
            <p className="text-xs text-muted-foreground truncate">{area.floor_name || ''} • {items.length} items</p>
          </div>
          {/* Work status, beside but not merged with the lock chip to its right:
              they are two different questions and were routinely read as one. */}
          <QaStatusMenu
            status={area.work_status ?? 'not_started'}
            onChange={handleAreaStatusChange}
            disabled={area.is_locked}
            targetLabel={area.area_name || area.name || 'area'}
          />
          <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.txt" className="hidden"
            onChange={(e) => { handleDocumentUpload(e.target.files); e.target.value = '' }} />
          <button onClick={() => fileInputRef.current?.click()} disabled={isUploadingDoc || area.is_locked} title="Upload document, photo, or file"
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            {isUploadingDoc ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
          </button>
          <button onClick={() => setLockModalOpen(true)}
            className={cn('flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors',
              area.is_locked ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
              : allComplete ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 animate-pulse'
              : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
            {area.is_locked ? <><Lock className="w-3 h-3" />Locked</> : <><Unlock className="w-3 h-3" />{allComplete ? 'Ready to Lock' : 'Active'}</>}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 pb-24 space-y-4">
        <ProgressBar items={items as ItemWithState[]} />

        {/* The owner's second signature over the whole area. In normal flow
            under the progress bar rather than in the sticky header (already at
            its width budget) or in CompletionSection (which only renders while
            the area is unlocked, hiding the signature exactly when it matters
            most). Read-only for non-verifiers. */}
        <div className="rounded-xl border border-border bg-card p-3">
          <QaOwnerVerification
            targetLabel={area.area_name || area.name || 'area'}
            owner_verified={area.owner_verified}
            owner_verified_at={area.owner_verified_at}
            owner_verified_by_name={area.owner_verified_by_name}
            can_verify_completion={area.can_verify_completion}
            onVerify={handleVerifyArea}
          />
        </div>

        {items.length === 0 && (
          <div className="text-center py-8 bg-card rounded-xl border border-border">
            <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">No Checklist Items</h2>
            <p className="text-muted-foreground mb-4">Apply a template to add items to this area.</p>
            <button onClick={() => setTemplateModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">
              <ListChecks className="w-4 h-4" />Apply Template
            </button>
            <button
              onClick={() => void addAreaItem(area.id).then(refreshItems)}
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border font-medium hover:bg-muted transition-colors"
            >
              <Plus className="w-4 h-4" />Add check
            </button>
          </div>
        )}

        {items.length > 0 && !area.is_locked && (
          <button onClick={() => setTemplateModalOpen(true)}
            className="w-full py-2 rounded-lg border border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-primary">
            <Plus className="w-4 h-4" /><span className="text-sm">Apply Another Template</span>
          </button>
        )}

        {items.length > 0 && (
          <div className="text-center text-xs text-muted-foreground hidden sm:block">
            Press <kbd className="px-1.5 py-0.5 bg-muted rounded border text-[10px] font-mono">P</kbd> Pass
            <kbd className="px-1.5 py-0.5 bg-muted rounded border text-[10px] font-mono ml-2">F</kbd> Fail
            <kbd className="px-1.5 py-0.5 bg-muted rounded border text-[10px] font-mono ml-2">N</kbd> N/A
            <span className="ml-2">• Swipe to action on mobile</span>
          </div>
        )}

        {items.length > 0 && (
          <QaStatusFilterChips counts={statusCounts} value={statusFilter} onChange={setStatusFilter} />
        )}

        {items.length > 0 && visibleItems.length === 0 && (
          <div className="rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
            No items with this status.
            <button onClick={() => setStatusFilter(null)} className="ml-1 text-primary hover:underline">Clear filter</button>
          </div>
        )}

        {visibleItems.length > 0 && (
          <div ref={listRef} className="space-y-3">
            {visibleItems.map(({ item, idx }) => (
              <ItemCard key={item.id} item={item as ItemWithState} isActive={idx === activeIndex} isLocked={area.is_locked}
                onStatusChange={handleStatusChange} onAddNote={handleAddNote} onEditNote={handleEditNote} onDeleteNote={handleDeleteNote}
                onReadingChange={handleReadingChange} onTakePhoto={handleTakePhoto}
                onCreateNCR={() => setNcrModalOpen(true)} onRequestHoldPoint={() => setHoldPointModalOpen(true)}
                evidence={evidence} onViewEvidence={setSelectedEvidence}
                onVerifyCompletion={(verified) => handleVerifyItem(item.id, verified)}
                areaId={area.id}
                onItemMutated={refreshItems}
                onPickPhotoFile={handlePickPhotoFile}
                onPickPhotoAsset={handlePickPhotoAsset}
                onDeleteEvidence={handleDeleteEvidence} />
            ))}
          </div>
        )}

        {items.length > 0 && allComplete && !area.is_locked && (
          <CompletionSection items={items} evidence={evidence} isUploadingDoc={isUploadingDoc}
            onUploadFiles={() => fileInputRef.current?.click()} onTakePhoto={() => setCameraOpen(true)}
            onRequestSignOff={() => setAreaSignOffModalOpen(true)} onLockArea={() => setLockModalOpen(true)} />
        )}

        {area.is_locked && (
          <div className="mt-6 p-4 rounded-xl border-2 border-amber-500/30 bg-amber-50 dark:bg-amber-900/10">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30"><Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" /></div>
              <div>
                <h3 className="font-semibold text-foreground">Area Locked</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  This area has been locked{area.locked_by_name ? ` by ${area.locked_by_name}` : ''}.
                  {area.lock_reason && ` Reason: ${area.lock_reason}`}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Floating Navigation */}
      {items.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-card rounded-full shadow-lg border border-border p-1.5 md:p-1">
          <button onClick={() => setActiveIndex(i => Math.max(i - 1, 0))} disabled={activeIndex === 0}
            className="p-3 rounded-full hover:bg-muted transition-colors disabled:opacity-50"><ChevronUp className="w-5 h-5" /></button>
          <span className="px-3 text-sm font-medium text-muted-foreground">{activeIndex + 1} / {items.length}</span>
          <button onClick={() => setActiveIndex(i => Math.min(i + 1, items.length - 1))} disabled={activeIndex === items.length - 1}
            className="p-3 rounded-full hover:bg-muted transition-colors disabled:opacity-50"><ChevronDown className="w-5 h-5" /></button>
        </div>
      )}

      {/* Camera Modal */}
      {cameraOpen && <CameraCapture attachTo={{ type: 'area', id: areaId! }} inspectionId={area?.inspection_id} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />}

      {/* Modals */}
      <ApplyTemplateModal isOpen={templateModalOpen} onClose={() => setTemplateModalOpen(false)} onApply={(id) => templateMutation.mutate(id)} templates={templates} isLoading={templateMutation.isPending} />
      <LockModal isOpen={lockModalOpen} isLocked={area.is_locked} onClose={() => setLockModalOpen(false)} onConfirm={(reason) => lockMutation.mutate({ isLocked: area.is_locked, reason })} isLoading={lockMutation.isPending} />
      <CreateNCRModal isOpen={ncrModalOpen} onClose={() => setNcrModalOpen(false)} onSubmit={(data) => ncrMutation.mutate(data)} itemTitle={items[activeIndex]?.title || ''} isLoading={ncrMutation.isPending} />
      <RequestHoldPointModal isOpen={holdPointModalOpen} onClose={() => setHoldPointModalOpen(false)} onSubmit={(name) => holdPointMutation.mutate(name)} itemTitle={items[activeIndex]?.title} isLoading={holdPointMutation.isPending} />
      <RequestHoldPointModal isOpen={areaSignOffModalOpen} onClose={() => setAreaSignOffModalOpen(false)} onSubmit={(name) => areaSignOffMutation.mutate(name)} itemTitle={`Area Sign-off: ${area?.area_name || area?.name || ''}`} isLoading={areaSignOffMutation.isPending} />

      {/* Evidence Viewer */}
      {selectedEvidence && <EvidenceViewer evidence={selectedEvidence} onClose={() => setSelectedEvidence(null)} />}
    </div>
  )
}
